export const isIFrame = (el: Element): el is HTMLIFrameElement => ('contentDocument' in el);
export const isHtmlElement = (el: Element): el is HTMLElement => (el instanceof HTMLElement);

const BLOCK_TAGS = new Set([
  'p', 'ul', 'ol', 'li', 'div', 'article', 'section',
  'header', 'footer', 'nav', 'main', 'aside',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'blockquote', 'pre', 'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td',
  'figure', 'figcaption', 'details', 'summary',
]);

const VOID_BLOCK_TAGS = new Set(['br', 'hr']);

/**
 * Serializes an Element's opening tag, including all its attributes.
 * @param el - The element to serialize.
 * @returns A string like `<div class="foo">`.
 */
const buildOpenTag = (el: Element): string => {
  const tag = el.tagName.toLowerCase();
  const attrs = Array.from(el.attributes)
    .map(({ name, value }) => `${name}="${value}"`)
    .join(' ');
  return attrs ? `<${tag} ${attrs}>` : `<${tag}>`;
};

/**
 * Returns true if any immediate child of `el` is a block-level element.
 * Used to decide whether a block element's children should be placed on
 * separate indented lines or kept inline with the parent tag.
 * @param el - The element whose children to inspect.
 */
const hasBlockChild = (el: Element): boolean => Array.from(el.childNodes).some(
  (child) => child.nodeType === Node.ELEMENT_NODE
    && BLOCK_TAGS.has((child as Element).tagName.toLowerCase()),
);

/**
 * Recursively serializes a DOM node to an indented HTML string.
 *
 * - Text nodes are trimmed and indented at the current depth.
 * - Void block elements (`br`, `hr`) are placed on their own line.
 * - Block elements whose children are all inline are kept on one line:
 *   `<li>Item text</li>`.
 * - Block elements with block children have their children indented one
 *   level deeper, with the closing tag on its own line.
 * - Inline elements are returned as-is via `outerHTML`.
 *
 * @param node - The DOM node to serialize.
 * @param depth - The current indentation depth (number of leading tabs).
 * @returns The serialized HTML string fragment for this node.
 */
const serializeNode = (node: Node, depth: number): string => {
  if (node.nodeType === Node.TEXT_NODE) {
    const trimmed = node.textContent?.trim() ?? '';
    return trimmed ? `\n${'\t'.repeat(depth)}${trimmed}` : '';
  }

  if (node.nodeType !== Node.ELEMENT_NODE) return '';

  const el = node as Element;
  const tagName = el.tagName.toLowerCase();
  const indent = '\t'.repeat(depth);

  if (VOID_BLOCK_TAGS.has(tagName)) {
    return `\n${indent}${buildOpenTag(el)}`;
  }

  if (BLOCK_TAGS.has(tagName)) {
    const openTag = buildOpenTag(el);
    const closeTag = `</${tagName}>`;
    if (!hasBlockChild(el)) {
      return `\n${indent}${openTag}${el.innerHTML}${closeTag}`;
    }
    const children = Array.from(el.childNodes)
      .map((child) => serializeNode(child, depth + 1))
      .join('');
    return `\n${indent}${openTag}${children}\n${indent}${closeTag}`;
  }

  return el.outerHTML;
};

/**
 * Parses an HTML string and returns a prettified version with block-level
 * elements on their own lines and nested content indented with tabs.
 *
 * Uses `DOMParser` to build a real DOM tree, so tag identification and
 * nesting are handled by the browser rather than regex.
 *
 * @param html - The raw HTML string to prettify.
 * @returns The indented HTML string.
 *
 * @example
 * prettifyHtml('<div><ul><li>Item 1</li><li>Item 2</li></ul></div>')
 * // <div>
 * //   <ul>
 * //     <li>Item 1</li>
 * //     <li>Item 2</li>
 * //   </ul>
 * // </div>
 */
export const prettifyHtml = (html: string): string => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return Array.from(doc.body.childNodes)
    .map((node) => serializeNode(node, 0))
    .join('')
    .trim();
};
